import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import axios from '../../lib/httpClient'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { selectVerified } from '@/public/redux/recaptchaSlice'
import { accessToken } from '@/shared/helpers/CSRF'
import { validationPatterns } from '@/shared/helpers/Errors'
import { datadogLogs } from '@datadog/browser-logs'

export default function CompanyNewsletterForm({
  downloadUrl = '', downloadName = '', buttonText = '', mailingList = '',
}) {
  const verified = useSelector(selectVerified)
  const {
    handleSubmit,
    reset,
    register,
    control,
    formState: { errors },
  } = useForm()
  const [message, setMessage] = useState('')

  const submit = async ({ interest, companySize, ...data }) => {
    const sendingData = {
      company_size: companySize ? companySize.value : '',
      download_url: downloadUrl,
      download_name: downloadName,
      mailing_list: mailingList,
      ...data,
    }
    try {
      const response = await axios({
        method: 'post',
        url: '/api/newsletter_signups',
        headers: {
          'Content-Type': 'application/json',
          Accepts: 'application/json',
          'X-CSRF-Token': accessToken,
        },
        data: sendingData,
      })
      if (!response.data.success) {
        datadogLogs.logger.error('Newsletter subscribe form error', response.data.last_error)
        setMessage('An error occurred. Please try again or contact us.')
        return
      }
      setMessage(downloadUrl && downloadUrl.length > 0 ? 'Thank you! Check your email for your download link.' : 'Thank you for signing up!')
      reset({
        first_name: '',
        last_name: '',
        company: '',
        email: '',
      }, {
        keepErrors: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: true,
      })
      window.setTimeout(() => {
        document.getElementById('newsletter-signup-modal')?.click()
        setMessage('')
      }, 6000)
    } catch (error) {
      setMessage('An error occurred. Please try again or contact us.')
      datadogLogs.logger.error('Newsletter subscribe form error', error)
    }
  }

  const companySizes = [
    { value: '50-100', label: '50-100' },
    { value: '100-500', label: '100-500' },
    { value: '500-1000', label: '500-1000' },
    { value: '1000-5000', label: '1000-5000' },
    { value: '5000+', label: '5000+' },
  ]

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="row">
        <div className="col-sm-6">
          <label
            htmlFor="newsletter-signup-first-name"
            className="form-label"
          >
            First Name
          </label>
          <input
            id="newsletter-signup-first-name"
            className={`form-control ${errors?.first_name ? 'error' : ''}`}
            {...register('first_name', { required: 'First name is required' })}
          />
          {errors?.first_name
            && <em className="text-danger"><small>{errors.first_name.message}</small></em>}
        </div>

        <div className="col-sm-6">
          <label
            htmlFor="newsletter-signup-last-name"
            className="form-label"
          >
            Last Name
          </label>
          <input
            id="newsletter-signup-last-name"
            className={`form-control ${errors?.last_name ? 'error' : ''}`}
            {...register('last_name', { required: 'Last name is required' })}
          />
          {errors?.last_name
            && <em className="text-danger"><small>{errors.last_name.message}</small></em>}
        </div>

      </div>
      <div className="row mt-3">
        <div className="col-sm-6">
          <label
            htmlFor="newsletter-signup-company"
            className="form-label"
          >
            Company
          </label>
          <input
            id="newsletter-signup-company"
            className={`form-control ${errors?.last_name ? 'error' : ''}`}
            {...register('company', { required: 'Company is required' })}
          />
          {errors?.company
          && <em className="text-danger"><small>{errors.company.message}</small></em>}
        </div>

        <div className="col-sm-6">
          <label
            htmlFor="newsletter-signup-company-size"
            className="form-label"
          >
            Company Size
          </label>
          <Controller
            control={control}
            name="companySize"
            rules={{ required: 'Company size is required' }}
            render={({
              field: {
                onChange, onBlur, value, name, ref,
              },
            }) => (
              <Select
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    height: '46px',
                    fontSize: '1rem',
                    borderRadius: '0.5rem',
                    border: '2px solid #7b796b',
                  }),
                }}
                classNames={{
                  control: () => `form-control ${errors?.last_name ? 'error' : ''}`,
                }}
                options={companySizes}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
              />
            )}
          />
          {errors?.companySize
            && <em className="text-danger"><small>{errors.companySize.message}</small></em>}
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-sm-8">
          <label
            htmlFor="newsletter-signup-email"
            className="form-label"
          >
            Email Address
          </label>
          <input
            id="newsletter-signup-email"
            className={`form-control ${errors?.email_address ? 'error' : ''}`}
            type="email"
            {...register('email_address', { required: 'Email address is required', pattern: validationPatterns.email })}
          />
          {errors?.email_address
            && <em className="text-danger"><small>{errors.email_address.message}</small></em>}
        </div>
        <div className="col-sm-4 news-letter-form text-center">
          <p className="mt-3 mb-n1">{message}</p>
          <button
            type="submit"
            className="btn btn-secondary btn-secondary-filled my-3"
            disabled={!verified}
          >
            {buttonText || 'Subscribe'}
          </button>
        </div>
      </div>
    </form>
  )
}
